import { useIntl } from 'react-intl'
import type { FieldsConfiguration } from 'msp-components'
import { isValidName } from 'msp-integrations'

const getAddressFields = (): FieldsConfiguration => {
  const { formatMessage } = useIntl()

  return {
    salutation: {
      id: 'salutation',
      type: 'radio',
      name: 'salutation',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Salutation',
        id: 'use_address_fields.label.salutation'
      }),
      options: [
        {
          id: 'mr',
          value: 'MR',
          label: formatMessage({
            defaultMessage: 'Mr',
            id: 'msp.label.salutation.mr'
          }),
          extraClassName: 'mr-2'
        },
        {
          id: 'mrs',
          value: 'MRS',
          label: formatMessage({
            defaultMessage: 'Mrs',
            id: 'msp.label.salutation.mrs'
          }),
          extraClassName: 'ml-6 mr-2'
        }
      ],
      rules: {
        required: formatMessage({
          defaultMessage: 'Please select your salutation.',
          id: 'msp.address_form.error.please_select_salutation'
        })
      },
      defaultValues: 'MRS'
    },
    firstName: {
      id: 'firstName',
      type: 'text',
      name: 'firstName',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'First name',
        id: 'msp.address_form.label.first_name'
      }),
      autoComplete: 'given-name',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your first name.',
          id: 'msp.address_form.error.required_first_name'
        }),
        validate: (value: string) =>
          isValidName(value) ||
          formatMessage({
            defaultMessage: 'Field contains invalid characters',
            id: 'msp.global.form.error.valid_name'
          })
      }
    },
    lastName: {
      id: 'lastName',
      type: 'text',
      name: 'lastName',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Last name',
        id: 'msp.address_form.label.last_name'
      }),
      autoComplete: 'family-name',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your last name.',
          id: 'msp.address_form.error.required_last_name'
        }),
        validate: (value: string) =>
          isValidName(value) ||
          formatMessage({
            defaultMessage: 'Field contains invalid characters',
            id: 'msp.global.form.error.valid_name'
          })
      }
    },
    companyName: {
      id: 'companyName',
      type: 'text',
      name: 'companyName',
      placeholder: '',
      label: formatMessage({
        defaultMessage: 'Company (optional)',
        id: 'msp.address_form.label.company'
      }),
      autoComplete: 'organization'
    },
    phone: {
      id: 'phone',
      name: 'phone',
      label: formatMessage({
        defaultMessage: 'Phone (optional)',
        id: 'msp.address_form.label.phone'
      }),
      type: 'tel',
      autoComplete: 'tel',
      inputProps: ({ onChange }: any) => ({
        inputMode: 'numeric',
        onChange(evt: any) {
          onChange(evt.target.value)
        }
      })
    },
    countryCode: {
      id: 'countryCode',
      name: 'countryCode',
      label: formatMessage({
        defaultMessage: 'Country',
        id: 'msp.address_form.label.country'
      }),
      type: 'select',
      autoComplete: 'country',
      options: [
        { id: 'DE', value: 'Germany' },
        { id: 'AT', value: 'Austria' }
      ],
      rules: {
        required: formatMessage({
          defaultMessage: 'Please select your country.',
          id: 'msp.address_form.error.please_select_your_country'
        })
      }
    },
    address1: {
      id: 'address1',
      name: 'address1',
      label: formatMessage({
        defaultMessage: 'Street + Housenumber',
        id: 'msp.address_form.label.address'
      }),
      defaultValue: '',
      type: 'text',
      autoComplete: 'street-address',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your address.',
          id: 'msp.address_form.error.please_select_your_address'
        })
      }
    },
    address2: {
      id: 'address2',
      name: 'address2',
      label: formatMessage({
        defaultMessage: 'Address complement (optional)',
        id: 'msp.address_form.label.address_2'
      }),
      defaultValue: '',
      type: 'text',
      autoComplete: 'address-line2'
    },
    city: {
      id: 'city',
      name: 'city',
      label: formatMessage({
        defaultMessage: 'City',
        id: 'msp.address_form.label.city'
      }),
      defaultValue: '',
      type: 'text',
      autoComplete: 'address-level2',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your city.',
          id: 'msp.address_form.error.please_select_your_city'
        })
      }
    },
    postalCode: {
      id: 'postalCode',
      name: 'postalCode',
      label: formatMessage({
        defaultMessage: 'Postal Code',
        id: 'msp.address_form.label.postal_code'
      }),
      defaultValue: '',
      type: 'text',
      autoComplete: 'postal-code',
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your postal code.',
          id: 'msp.address_form.error.please_enter_your_postal'
        })
      }
    }
  }
}

export default getAddressFields
